// Booking questions: the client mirror of the database rules in
// supabase/migrations/20260926090020_booking_questions_rpcs.sql.
//
// A service asks questions of EACH guest (services.config.participant_fields,
// the existing key) and, new, questions ONCE per booking
// (services.config.booking_fields). A field is the old
// {key, label, type, required, options} plus optional help, a condition
// (show_when), exclusive multiselect answers, a sensitive flag, an email/phone
// format and a number range. The database is the authority; this module lets
// every surface (widget, pay page, back office, staff app) say the same thing
// BEFORE a round trip. The named cases in supabase/tests/outrides_booking_form.sql
// run against both, so the two cannot drift (e2e/booking-questions-logic.spec.ts).
//
// POPIA: a field marked `sensitive` holds special personal information (health,
// a minor's guardian). Its answers are returned to manager+ only; nothing in
// this module stores or logs an answer.

import {
  CLIENT_ROW_KEY,
  PARTICIPANT_ID_KEY,
  PICK_KEY,
  REMOVE_KEY,
  RESERVED_ROW_KEYS,
  SAME_PERSON_KEY,
  SELF_KEY,
  isParticipantId,
  isRowMarkerKey,
  isTombstone,
  samePersonHolds,
} from "./participant-ids";

export const QUESTION_TYPES = [
  "text",
  "number",
  "select",
  "multiselect",
  "yesno",
  "checkbox",
  "longtext",
] as const;
export type QuestionType = (typeof QUESTION_TYPES)[number];

export type ShowWhen =
  | { field: string; equals: string | number | boolean }
  | { field: string; lt: number }
  | { field: string; gte: number };

export type QuestionField = {
  key: string;
  label: string;
  type: QuestionType;
  required: boolean;
  options?: string[];
  help?: string | null;
  show_when?: ShowWhen | null;
  exclusive_options?: string[] | null;
  sensitive?: boolean | null;
  /** Set by the SERVER on the definitions a booking reader returns
   *  (get_booking_participants, get_service_day_sheet, and since
   *  20260927150010 the cart and pay-link readers), never saved on a
   *  service: the service no longer asks this question and nothing marks it
   *  sensitive. Below manager its answer is withheld all the same (the field
   *  then also carries sensitive: true), so it is worded "no longer asked",
   *  never "medical and guardian", and gets no Sensitive badge. */
  retired?: boolean | null;
  format?: "email" | "phone" | null;
  min?: number | null;
  max?: number | null;
  /** Per-person list only (20260927130010): this question holds the person's
   *  first name, surname or full name. Only on a required short-text question
   *  with no email/phone check, no condition, not sensitive and not about a
   *  guardian; each part at most once, and a full name replaces the other
   *  two. Every per-person list names a full name or a first name
   *  (identityProblem). Set by the operator or a template, never read from a
   *  label. */
  profile?: ProfileSlot | null;
  /** Per-person list only: a question about the parent or guardian of a
   *  person under 18 (how "a minor" is known later). */
  guardian?: boolean | null;
  /** Per-person list only. false: never kept for next time (an age, a consent
   *  tick): asked afresh on every booking. Absent = may be kept. */
  remember?: boolean | null;
};

/** The parts of a person's name a question can hold (QuestionField.profile). */
export const PROFILE_SLOTS = ["first_name", "last_name", "full_name"] as const;
export type ProfileSlot = (typeof PROFILE_SLOTS)[number];

export type Answer = string | number | boolean | string[];
export type AnswerRow = Record<string, Answer | null | undefined>;
export type GuestNoun = { one: string; other: string };

export type CheckoutConfirmations = {
  version_id: string;
  version: number;
  hash: string;
  statements: string[];
  terms: { title: string | null; text: string | null; url: string | null } | null;
};

/** One element of get_cart_booking_participants.bookings and of
 *  get_payment_link_participants.bookings (identical shapes). */
export type BookingForm = {
  booking_id: string;
  service_id: string | null;
  service_name: string | null;
  option_name: string | null;
  party_size: number;
  starts_at: string | null;
  /** The earliest form row of the visit (same cart, service and start). */
  visit_key: string;
  guest_noun: GuestNoun;
  fields: QuestionField[];
  participants: AnswerRow[];
  complete: boolean;
  group_fields: QuestionField[];
  group_answers: AnswerRow;
  group_complete: boolean;
  confirmations: CheckoutConfirmations | null;
  confirmations_accepted: boolean;
  /** Legacy shape only: the WhatsApp bot may collect it in chat. */
  chat_collectable: boolean;
  ready: boolean;
  /** Sensitive answers were withheld from this caller (derived from the
   *  field definitions, never from the answers). The guest readers (cart and
   *  pay link) withhold them from everyone below manager — a staff member can
   *  hold either token — so a stored form reads complete with its sensitive
   *  answers absent; a guest save that leaves those keys out keeps the stored
   *  answers. Below manager a question counts as sensitive the way the
   *  booking page counts it (20260927150010): marked sensitive today, even
   *  on a booking saved before the flag, or no longer asked (retired); the
   *  definitions returned carry sensitive: true on each. */
  sensitive_withheld?: boolean;
};

export const QUESTION_LIMITS = {
  participantFields: 24,
  bookingFields: 12,
  options: 20,
  optionLength: 100,
  keyLength: 40,
  labelLength: 100,
  helpLength: 300,
  textLength: 500,
  longtextLength: 2000,
  emailLength: 254,
  numberMax: 1_000_000,
  statements: 10,
  statementLength: 300,
  termsTitleLength: 100,
  termsTextLength: 20_000,
  termsUrlLength: 500,
  nounLength: 30,
} as const;

export const DEFAULT_GUEST_NOUN: GuestNoun = { one: "guest", other: "guests" };

const KEY_RE = /^[a-z][a-z0-9_]{0,39}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^\+?[0-9][0-9 ()-]{6,19}$/;
const HTTPS_RE = /^https:\/\/\S+$/;

// ─── small helpers that match Postgres ──────────────────────────────────────
/** Postgres btrim(): spaces only. */
function btrim(s: string): string {
  return s.replace(/^ +/, "").replace(/ +$/, "");
}
/** Postgres length(): characters (code points), not UTF-16 units. */
function pgLength(s: string): number {
  return [...s].length;
}
function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}
/** jsonb `f ? 'k' and f->'k' <> 'null'`: present and not null. */
function has(o: Record<string, unknown>, k: string): boolean {
  return Object.prototype.hasOwnProperty.call(o, k) && o[k] !== null && o[k] !== undefined;
}
function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((x, i) => jsonEqual(x, b[i]));
  }
  if (isObject(a) && isObject(b)) {
    const ka = Object.keys(a);
    return ka.length === Object.keys(b).length && ka.every((k) => jsonEqual(a[k], b[k]));
  }
  return false;
}
function capitalize(s: string): string {
  return s.length ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

// ─── definitions ────────────────────────────────────────────────────────────
export type QuestionFieldsProblemCode =
  | "too_many"
  | "bad_key"
  | "bad_label"
  | "bad_type"
  | "bad_required"
  | "bad_options"
  | "bad_help"
  | "bad_show_when"
  | "bad_exclusive"
  | "bad_sensitive"
  | "bad_format"
  | "bad_range"
  | "duplicate_key";

export type QuestionFieldsProblem = {
  /** 0-based position in the list; null when the list itself is not a list. */
  index: number | null;
  key: string | null;
  code: QuestionFieldsProblemCode;
};

/**
 * NULL when `fields` is a valid list of at most `max` questions, else the
 * FIRST problem — the same answer as the database's _question_fields_problem.
 * Use QUESTION_LIMITS.participantFields (24) or .bookingFields (12) for max.
 */
export function questionFieldsProblem(fields: unknown, max: number): QuestionFieldsProblem | null {
  if (!Array.isArray(fields)) return { index: null, key: null, code: "bad_type" };
  if (fields.length > max) {
    const extra = fields[max];
    const key = isObject(extra) && extra.key != null ? String(extra.key) : null;
    return { index: max, key, code: "too_many" };
  }
  const seen: string[] = [];
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i];
    if (!isObject(f) || typeof f.key !== "string" || !KEY_RE.test(f.key)) {
      return { index: i, key: null, code: "bad_key" };
    }
    const key = f.key;
    const problem = (code: QuestionFieldsProblemCode): QuestionFieldsProblem => ({ index: i, key, code });
    if (typeof f.label !== "string" || !between(pgLength(btrim(f.label)), 1, QUESTION_LIMITS.labelLength)) {
      return problem("bad_label");
    }
    const t = typeof f.type === "string" ? f.type : null;
    if (t === null || !(QUESTION_TYPES as readonly string[]).includes(t)) return problem("bad_type");
    if (typeof f.required !== "boolean") return problem("bad_required");

    if (t === "select" || t === "multiselect") {
      const o = f.options;
      if (
        !Array.isArray(o) ||
        !between(o.length, 1, QUESTION_LIMITS.options) ||
        o.some((x) => typeof x !== "string" || !between(pgLength(btrim(x)), 1, QUESTION_LIMITS.optionLength)) ||
        (t === "multiselect" && new Set(o).size !== o.length)
      ) {
        return problem("bad_options");
      }
    }

    if (has(f, "help")) {
      if (typeof f.help !== "string" || !between(pgLength(btrim(f.help)), 1, QUESTION_LIMITS.helpLength)) {
        return problem("bad_help");
      }
    }

    // This field's controller (found below when it has a show_when); the
    // sensitive rule further down needs it too.
    let ctrl: Record<string, unknown> | null = null;
    if (has(f, "show_when")) {
      const sw = f.show_when;
      if (isObject(sw) && typeof sw.field === "string") {
        for (let j = 0; j < i; j++) {
          const e = fields[j];
          if (isObject(e) && e.key === sw.field) {
            ctrl = e;
            break;
          }
        }
      }
      const ops = isObject(sw) ? ["equals", "lt", "gte"].filter((k) => Object.prototype.hasOwnProperty.call(sw, k)).length : 0;
      const ctype = ctrl && typeof ctrl.type === "string" ? ctrl.type : "";
      let bad = !ctrl || !isObject(sw) || ops !== 1 || (ctrl !== null && has(ctrl, "show_when"));
      bad = bad || !["select", "yesno", "number", "checkbox"].includes(ctype);
      if (!bad && isObject(sw) && Object.prototype.hasOwnProperty.call(sw, "equals")) {
        const eq = sw.equals;
        const opts = ctrl && Array.isArray(ctrl.options) ? ctrl.options : [];
        const ok =
          ctype === "select"
            ? typeof eq === "string" && opts.includes(eq)
            : ctype === "yesno"
              ? typeof eq === "boolean"
              : ctype === "number"
                ? typeof eq === "number"
                : ctype === "checkbox"
                  ? eq === true
                  : false;
        bad = !ok;
      }
      if (
        !bad &&
        isObject(sw) &&
        (Object.prototype.hasOwnProperty.call(sw, "lt") || Object.prototype.hasOwnProperty.call(sw, "gte"))
      ) {
        const bound = Object.prototype.hasOwnProperty.call(sw, "lt") ? sw.lt : sw.gte;
        bad = ctype !== "number" || typeof bound !== "number";
      }
      if (bad) return problem("bad_show_when");
    }

    if (has(f, "exclusive_options")) {
      const x = f.exclusive_options;
      const opts = Array.isArray(f.options) ? f.options : [];
      if (
        t !== "multiselect" ||
        !Array.isArray(x) ||
        x.length === 0 ||
        x.some((o) => typeof o !== "string" || !opts.includes(o))
      ) {
        return problem("bad_exclusive");
      }
    }

    if (has(f, "sensitive") && typeof f.sensitive !== "boolean") return problem("bad_sensitive");
    // A question shown only for a sensitive answer is sensitive itself: its
    // mere presence says how the sensitive question was answered.
    if (has(f, "show_when") && ctrl !== null && ctrl.sensitive === true && f.sensitive !== true) {
      return problem("bad_sensitive");
    }

    if (has(f, "format")) {
      if (t !== "text" || (f.format !== "email" && f.format !== "phone")) return problem("bad_format");
    }

    if (has(f, "min") || has(f, "max")) {
      const inRange = (v: unknown) => typeof v === "number" && v >= 0 && v <= QUESTION_LIMITS.numberMax;
      if (
        t !== "number" ||
        (has(f, "min") && !inRange(f.min)) ||
        (has(f, "max") && !inRange(f.max)) ||
        (typeof f.min === "number" && typeof f.max === "number" && f.min > f.max)
      ) {
        return problem("bad_range");
      }
    }

    if (seen.includes(key)) return problem("duplicate_key");
    seen.push(key);
  }
  return null;
}

function between(n: number, lo: number, hi: number): boolean {
  return n >= lo && n <= hi;
}

// ─── the person's name (profile markers) ────────────────────────────────────
export type IdentityProblemCode =
  | "bad_identity"
  | "duplicate_identity"
  | "missing_identity"
  | "bad_guardian"
  | "bad_remember";

export type IdentityProblem = {
  list: "participant" | "booking";
  /** 0-based position in that list; null for missing_identity (the list). */
  index: number | null;
  key: string | null;
  code: IdentityProblemCode;
};

/**
 * NULL when both lists mark the person's name correctly, else the FIRST
 * problem: the same answer as the database's _question_identity_problem
 * (20260927130010). Run it after questionFieldsProblem accepts both lists.
 *   bad_identity        a `profile` that is not first_name / last_name /
 *                       full_name, or on a question that is not required
 *                       short text without an email/phone check, without a
 *                       condition, not sensitive and not a guardian question;
 *                       or on a once-per-booking question
 *   duplicate_identity  a part of the name held twice, or a full name beside
 *                       a first name or surname
 *   bad_guardian        `guardian` not true/false, or on a once-per-booking
 *                       question
 *   bad_remember        `remember` not true/false, or on a once-per-booking
 *                       question
 *   missing_identity    a per-person list with questions but no full-name or
 *                       first-name question
 * Labels are never read.
 */
export function identityProblem(participant: unknown, booking: unknown): IdentityProblem | null {
  const perPerson = Array.isArray(participant) ? participant : [];
  const once = Array.isArray(booking) ? booking : [];
  const slots = new Set<string>();
  for (let i = 0; i < perPerson.length; i++) {
    const f = perPerson[i];
    if (!isObject(f)) continue;
    const at = (code: IdentityProblemCode): IdentityProblem => ({
      list: "participant",
      index: i,
      key: typeof f.key === "string" ? f.key : null,
      code,
    });
    if (has(f, "profile")) {
      const slot = f.profile;
      if (typeof slot !== "string" || !(PROFILE_SLOTS as readonly string[]).includes(slot)) return at("bad_identity");
      if (
        f.type !== "text" ||
        has(f, "format") ||
        f.required !== true ||
        f.sensitive === true ||
        has(f, "show_when") ||
        f.guardian === true
      ) {
        return at("bad_identity");
      }
      if (
        slots.has(slot) ||
        (slot === "full_name" && (slots.has("first_name") || slots.has("last_name"))) ||
        (slot !== "full_name" && slots.has("full_name"))
      ) {
        return at("duplicate_identity");
      }
      slots.add(slot);
    }
    if (has(f, "guardian") && typeof f.guardian !== "boolean") return at("bad_guardian");
    if (has(f, "remember") && typeof f.remember !== "boolean") return at("bad_remember");
  }
  for (let i = 0; i < once.length; i++) {
    const f = once[i];
    if (!isObject(f)) continue;
    const at = (code: IdentityProblemCode): IdentityProblem => ({
      list: "booking",
      index: i,
      key: typeof f.key === "string" ? f.key : null,
      code,
    });
    if (has(f, "profile")) return at("bad_identity");
    if (has(f, "guardian")) return at("bad_guardian");
    if (has(f, "remember")) return at("bad_remember");
  }
  if (perPerson.length > 0 && !slots.has("first_name") && !slots.has("full_name")) {
    return { list: "participant", index: null, key: null, code: "missing_identity" };
  }
  return null;
}

/**
 * The profile, guardian and remember flags of a raw definition, narrowed to
 * what they mean (a marker the rule knows, guardian true, remember false).
 * For every client that rebuilds a field object key by key, so the flags are
 * never silently dropped (#928).
 */
export function profileFlagsOf(v: Record<string, unknown>): Pick<QuestionField, "profile" | "guardian" | "remember"> {
  const out: Pick<QuestionField, "profile" | "guardian" | "remember"> = {};
  if (typeof v.profile === "string" && (PROFILE_SLOTS as readonly string[]).includes(v.profile)) {
    out.profile = v.profile as ProfileSlot;
  }
  if (v.guardian === true) out.guardian = true;
  if (v.remember === false) out.remember = false;
  return out;
}

/** Can this question hold the person's name? (The placement half of bad_identity.) */
export function canHoldName(
  field: Pick<QuestionField, "type" | "format" | "required" | "sensitive" | "show_when" | "guardian">,
): boolean {
  return (
    field.type === "text" &&
    !field.format &&
    field.required === true &&
    field.sensitive !== true &&
    !field.show_when &&
    field.guardian !== true
  );
}

// ─── answers ────────────────────────────────────────────────────────────────
/** Is this question shown for this row? One level: the controlling field is
 *  an earlier question of the same list, and never conditional itself. */
export function isFieldVisible(field: Pick<QuestionField, "show_when">, row: AnswerRow | null | undefined): boolean {
  const sw = field.show_when as unknown;
  if (!isObject(sw)) return true;
  const c = isObject(row) && typeof sw.field === "string" ? (row as Record<string, unknown>)[sw.field] : undefined;
  if (Object.prototype.hasOwnProperty.call(sw, "equals")) {
    return c !== undefined && jsonEqual(c, sw.equals);
  }
  if (Object.prototype.hasOwnProperty.call(sw, "lt")) {
    return typeof c === "number" && typeof sw.lt === "number" && c < sw.lt;
  }
  if (Object.prototype.hasOwnProperty.call(sw, "gte")) {
    return typeof c === "number" && typeof sw.gte === "number" && c >= sw.gte;
  }
  return true;
}

/** Absent, null, "", [] for a multiselect, false for a checkbox. false IS an
 *  answer for yes/no. */
export function isEmptyAnswer(field: Pick<QuestionField, "type">, value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === "" ||
    (field.type === "multiselect" && Array.isArray(value) && value.length === 0) ||
    (field.type === "checkbox" && value === false)
  );
}

export type AnswerProblemCode =
  | "required"
  | "number"
  | "range"
  | "too_long"
  | "choice"
  | "email"
  | "phone"
  | "exclusive"
  | "hidden";

/**
 * The problem with one answer, or null. Pass the whole row to judge a
 * conditional question: a HIDDEN question must be unanswered ('hidden').
 * Mirrors the database exactly, so run it on questionsPayload(...) — the
 * trimmed, coerced values the server will receive — not on raw input text.
 */
export function answerProblem(
  field: QuestionField,
  value: unknown,
  row?: AnswerRow | null,
): AnswerProblemCode | null {
  if (row !== undefined && !isFieldVisible(field, row)) {
    return isEmptyAnswer(field, value) ? null : "hidden";
  }
  if (isEmptyAnswer(field, value)) return field.required ? "required" : null;
  switch (field.type) {
    case "number": {
      if (typeof value !== "number" || !Number.isFinite(value)) return "number";
      const ranged = typeof field.min === "number" || typeof field.max === "number";
      const lo = typeof field.min === "number" ? field.min : 0;
      const hi = typeof field.max === "number" ? field.max : QUESTION_LIMITS.numberMax;
      if (value < lo || value > hi) return ranged ? "range" : "number";
      return null;
    }
    case "yesno":
      return typeof value === "boolean" ? null : "choice";
    case "checkbox":
      return value === true ? null : "choice";
    case "multiselect": {
      if (!Array.isArray(value) || value.length > QUESTION_LIMITS.options) return "choice";
      const options = field.options ?? [];
      if (value.some((x) => typeof x !== "string" || !options.includes(x))) return "choice";
      if (new Set(value).size !== value.length) return "choice";
      const exclusive = field.exclusive_options ?? [];
      if (value.length > 1 && value.some((x) => exclusive.includes(x as string))) return "exclusive";
      return null;
    }
    case "longtext": {
      if (typeof value !== "string") return "choice";
      const n = pgLength(btrim(value));
      if (n === 0) return "required";
      return n > QUESTION_LIMITS.longtextLength ? "too_long" : null;
    }
    default: {
      // text and select: the rules as before
      if (typeof value !== "string") return "choice";
      const n = pgLength(btrim(value));
      if (n === 0) return "required";
      if (n > QUESTION_LIMITS.textLength) return "too_long";
      if (field.type === "select") return (field.options ?? []).includes(value) ? null : "choice";
      if (field.format === "email") {
        return EMAIL_RE.test(value) && pgLength(value) <= QUESTION_LIMITS.emailLength ? null : "email";
      }
      if (field.format === "phone") return PHONE_RE.test(value) ? null : "phone";
      return null;
    }
  }
}

export type FormProblemCode = AnswerProblemCode | "count" | "unknown";
export type FormProblem = {
  /** Row index; -1 when the number of rows is wrong. */
  row: number;
  key: string;
  code: FormProblemCode;
};

/**
 * The reserved key a row carries wrongly, or null (rider-ID spec §3.1 and
 * §4.3, guest-profile spec §4.1): an id that is not a uuid; "same person"
 * other than true or on a row without an id; "this is me" or a pick on a
 * row that has an id, or both at once; a tombstone that is anything but
 * {_pid, _remove: true}. The client-only _key is never judged (never sent).
 */
function rowMarkerProblem(row: Record<string, unknown>): string | null {
  const id = row[PARTICIPANT_ID_KEY];
  const hasId = id !== undefined && id !== null;
  if (hasId && !isParticipantId(id)) return PARTICIPANT_ID_KEY;
  if (isTombstone(row)) {
    const keys = Object.keys(row).filter((k) => k !== CLIENT_ROW_KEY);
    return hasId && row[REMOVE_KEY] === true && keys.length === 2 ? null : REMOVE_KEY;
  }
  if (Object.prototype.hasOwnProperty.call(row, SAME_PERSON_KEY) && (row[SAME_PERSON_KEY] !== true || !hasId)) {
    return SAME_PERSON_KEY;
  }
  const self = row[SELF_KEY];
  if (self !== undefined && (hasId || (self !== true && !isParticipantId(self)) || row[PICK_KEY] !== undefined)) return SELF_KEY;
  const pick = row[PICK_KEY];
  if (pick !== undefined && (hasId || !isParticipantId(pick))) return PICK_KEY;
  return null;
}

/**
 * Every problem in a set of rows, in row then field order (the first one is
 * what a form shows). Empty = what the server's _valid_booking_participants
 * accepts. For the once-per-booking questions pass [answers] and count 1.
 *
 * Rider numbers (participant-ids.ts): the reserved row keys (_pid and the
 * markers a save may carry) and the client-only _key are not answers, so
 * they are never "unknown"; a row carrying one wrongly is. A tombstone
 * removes a saved person: it is not one of the `count` and has no answers.
 * `row` is the index in `rows` as given. A row that is only an id (a
 * placeholder, {_pid}) is judged exactly like {}.
 */
export function formProblems(fields: QuestionField[], rows: unknown, count: number): FormProblem[] {
  if (!Array.isArray(rows) || rows.filter((r) => !isTombstone(r)).length !== count) {
    return [{ row: -1, key: "", code: "count" }];
  }
  const keys = new Set(fields.map((f) => f.key));
  const out: FormProblem[] = [];
  rows.forEach((row, i) => {
    if (!isObject(row)) {
      out.push({ row: i, key: "", code: "unknown" });
      return;
    }
    const marker = rowMarkerProblem(row);
    if (marker) out.push({ row: i, key: marker, code: "unknown" });
    if (isTombstone(row)) return;
    for (const k of Object.keys(row)) {
      if (!keys.has(k) && !isRowMarkerKey(k)) out.push({ row: i, key: k, code: "unknown" });
    }
    for (const field of fields) {
      const code = answerProblem(field, row[field.key], row as AnswerRow);
      if (code) out.push({ row: i, key: field.key, code });
    }
  });
  return out;
}

/** Does any question have to be answered? (The legacy "complete" shortcut:
 *  a form with no required question is complete.) */
export function requiresAnything(fields: Pick<QuestionField, "required">[]): boolean {
  return fields.some((f) => f.required === true);
}

function coerce(field: QuestionField, raw: unknown): unknown {
  if (raw === undefined || raw === null) return raw;
  switch (field.type) {
    case "number":
      if (typeof raw === "number") return raw;
      return String(raw).trim() === "" ? "" : Number(raw);
    case "yesno":
      if (raw === "true") return true;
      if (raw === "false") return false;
      return raw;
    case "checkbox":
      if (raw === "true") return true;
      if (raw === "false") return false;
      return raw;
    case "multiselect": {
      if (!Array.isArray(raw)) return raw;
      const options = field.options ?? [];
      const picked = new Set(raw.map((x) => String(x)));
      // In the order the operator wrote the options, whatever order ticked.
      return options.filter((o) => picked.has(o)).concat([...picked].filter((x) => !options.includes(x)));
    }
    default:
      return typeof raw === "string" ? raw.trim() : raw;
  }
}

/**
 * What to send: per row, only configured keys; strings trimmed, numbers
 * coerced, multiselect in option order; EMPTY answers and answers to HIDDEN
 * questions dropped (data minimisation — medical details go when the answer
 * is changed to No, guardian details when the age is 18 or more).
 *
 * Rider numbers (participant-ids.ts): the reserved row keys the row carries
 * (_pid, _same_person, _self, _pick) go through unchanged, and a tombstone
 * is sent as exactly {_pid, _remove}. The client-only _key never goes. A
 * row without any of them (every row, until the server issues ids) is sent
 * exactly as before.
 */
export function questionsPayload(fields: QuestionField[], rows: AnswerRow[]): AnswerRow[] {
  return rows.map((row) => {
    if (isTombstone(row)) return { [PARTICIPANT_ID_KEY]: row[PARTICIPANT_ID_KEY], [REMOVE_KEY]: row[REMOVE_KEY] } as AnswerRow;
    const coerced: AnswerRow = {};
    for (const field of fields) {
      const v = coerce(field, row?.[field.key]);
      if (v !== undefined) coerced[field.key] = v as Answer | null;
    }
    const out: AnswerRow = {};
    for (const field of fields) {
      const v = coerced[field.key];
      if (isEmptyAnswer(field, v)) continue;
      if (!isFieldVisible(field, coerced)) continue;
      out[field.key] = v;
    }
    for (const key of RESERVED_ROW_KEYS) {
      const v = row?.[key];
      if (key !== REMOVE_KEY && v !== undefined && v !== null) out[key] = v;
    }
    // A "same person" given for another name than the row now holds no
    // longer counts (samePersonHolds, P4a): the question is asked again.
    if (SAME_PERSON_KEY in out && !samePersonHolds(row)) delete out[SAME_PERSON_KEY];
    return out;
  });
}

/** The once-per-booking answers to send. They are the visit's, never a
 *  person's: no reserved row key is ever sent with them. */
export function groupPayload(fields: QuestionField[], answers: AnswerRow | null | undefined): AnswerRow {
  const out = questionsPayload(fields, [answers ?? {}])[0] ?? {};
  for (const key of RESERVED_ROW_KEYS) delete out[key];
  return out;
}

/**
 * Is this form ready to pay for? The same rule as the database's
 * _booking_form_state: every required visible answer per guest and per
 * booking, and the confirmations accepted (or, on the client, ticked).
 */
export function formReady(
  form: Pick<BookingForm, "fields" | "participants" | "party_size" | "group_fields" | "group_answers" | "confirmations" | "confirmations_accepted">,
  opts: { confirmationsTicked?: boolean } = {},
): boolean {
  const complete =
    !requiresAnything(form.fields) ||
    formProblems(form.fields, form.participants, form.party_size).length === 0;
  const groupComplete =
    !requiresAnything(form.group_fields) ||
    formProblems(form.group_fields, [form.group_answers ?? {}], 1).length === 0;
  const accepted = !form.confirmations || form.confirmations_accepted === true || opts.confirmationsTicked === true;
  return complete && groupComplete && accepted;
}

// ─── checkout confirmations ─────────────────────────────────────────────────
export type ConfirmationsProblem = {
  reason: "invalid_statements" | "invalid_terms";
  index: number | null;
  code: "too_many" | "bad_statement" | "duplicate" | "title" | "text" | "url" | "terms_need_a_statement";
};

/** The checks save_service_checkout_confirmations makes, before the round trip. */
export function confirmationsProblem(
  statements: unknown,
  terms: { title?: string | null; text?: string | null; url?: string | null } | null | undefined,
): ConfirmationsProblem | null {
  const list = statements ?? [];
  if (!Array.isArray(list)) return { reason: "invalid_statements", index: null, code: "bad_statement" };
  if (list.length > QUESTION_LIMITS.statements) {
    return { reason: "invalid_statements", index: QUESTION_LIMITS.statements, code: "too_many" };
  }
  const trimmed: string[] = [];
  for (let i = 0; i < list.length; i++) {
    const s = list[i];
    if (typeof s !== "string" || !between(pgLength(btrim(s)), 1, QUESTION_LIMITS.statementLength)) {
      return { reason: "invalid_statements", index: i, code: "bad_statement" };
    }
    const t = btrim(s);
    if (trimmed.includes(t)) return { reason: "invalid_statements", index: i, code: "duplicate" };
    trimmed.push(t);
  }
  const title = btrim(terms?.title ?? "");
  const text = btrim(terms?.text ?? "");
  const url = btrim(terms?.url ?? "");
  if (title && pgLength(title) > QUESTION_LIMITS.termsTitleLength) return { reason: "invalid_terms", index: null, code: "title" };
  if (text && pgLength(text) > QUESTION_LIMITS.termsTextLength) return { reason: "invalid_terms", index: null, code: "text" };
  if (url && (pgLength(url) > QUESTION_LIMITS.termsUrlLength || !HTTPS_RE.test(url))) {
    return { reason: "invalid_terms", index: null, code: "url" };
  }
  if ((title || text || url) && trimmed.length === 0) {
    return { reason: "invalid_statements", index: null, code: "terms_need_a_statement" };
  }
  return null;
}

// ─── words ──────────────────────────────────────────────────────────────────
/** "rider" for one, "riders" otherwise. */
export function nounFor(noun: GuestNoun | null | undefined, n: number): string {
  const g = noun && noun.one && noun.other ? noun : DEFAULT_GUEST_NOUN;
  return n === 1 ? g.one : g.other;
}

/** "Rider 2": the heading of the second guest's block. */
export function guestHeading(noun: GuestNoun | null | undefined, index: number): string {
  return `${capitalize(nounFor(noun, 1))} ${index + 1}`;
}

const LEGACY_WORDING: QuestionType[] = ["text", "number", "select", "longtext"];

/**
 * One problem in words. With the default noun, required / number / too_long /
 * choice on a text, number or select question read exactly as the widget's
 * participantError did ("Guest 1: enter rider name."). rowIndex null = a
 * once-per-booking question ("Booking details: ...").
 */
export function describeQuestionProblem(
  problem: Pick<FormProblem, "code" | "key">,
  field: QuestionField | null | undefined,
  noun: GuestNoun | null | undefined,
  rowIndex: number | null,
): string {
  if (problem.code === "count") return `Please enter details for each ${nounFor(noun, 1)}.`;
  const prefix = rowIndex === null ? "Booking details: " : `${guestHeading(noun, rowIndex)}: `;
  if (!field) return `${prefix}remove the unexpected answer "${problem.key}".`;
  const lower = field.label.toLowerCase();
  switch (problem.code) {
    case "required":
      if (field.type === "checkbox") return `${prefix}tick "${field.label}".`;
      if (!LEGACY_WORDING.includes(field.type)) return `${prefix}answer "${field.label}".`;
      return `${prefix}enter ${lower}.`;
    case "number":
      return `${prefix}enter a valid number for ${lower}.`;
    case "range":
      return `${prefix}enter a number between ${typeof field.min === "number" ? field.min : 0} and ${
        typeof field.max === "number" ? field.max : QUESTION_LIMITS.numberMax
      } for ${lower}.`;
    case "too_long":
      return `${prefix}keep ${lower} under ${
        field.type === "longtext" ? QUESTION_LIMITS.longtextLength : QUESTION_LIMITS.textLength
      } characters.`;
    case "choice":
      if (field.type === "yesno" || field.type === "multiselect") return `${prefix}answer "${field.label}".`;
      if (field.type === "checkbox") return `${prefix}tick "${field.label}".`;
      return `${prefix}choose ${lower} from the list.`;
    case "email":
      return `${prefix}enter a valid email address for ${lower}.`;
    case "phone":
      return `${prefix}enter a valid phone number for ${lower}.`;
    case "exclusive": {
      const only = field.exclusive_options?.[0] ?? "That answer";
      return `${prefix}"${only}" can't be combined with other answers to "${field.label}".`;
    }
    case "hidden":
      return `${prefix}remove the answer to ${lower}.`;
    case "unknown":
    default:
      return `${prefix}remove the unexpected answer "${problem.key}".`;
  }
}

/** An answer as the riding team reads it: Yes/No, comma-joined choices in the
 *  operator's order, "Confirmed" for a tick. Empty = "". */
export function formatAnswer(field: QuestionField, value: unknown): string {
  if (isEmptyAnswer(field, value)) return "";
  switch (field.type) {
    case "yesno":
      return value === true ? "Yes" : value === false ? "No" : String(value);
    case "checkbox":
      return value === true ? "Confirmed" : "";
    case "multiselect": {
      if (!Array.isArray(value)) return String(value);
      const options = field.options ?? [];
      const picked = value.map(String);
      return options
        .filter((o) => picked.includes(o))
        .concat(picked.filter((x) => !options.includes(x)))
        .join(", ");
    }
    default:
      return String(value);
  }
}
